import classNames from 'classnames';
import * as React from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { TextOrComponent } from '~/Features/Types/TextOrComponent';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import type { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import type { Theme } from '~/Utils/Theme';
import styles from './SubjectsContainer.module.css';

export interface SubjectContainerHeading {
    label: TextOrComponent;
    description?: TextOrComponent;
}

export interface SubjectContainerFooterAction {
    label: string;
    type: Exclude<ButtonType, ButtonType.Clear>;
    onClick: () => void;
}

export interface SubjectContainerFooter {
    action: SubjectContainerFooterAction;
    label?: TextOrComponent;
}

export interface SubjectsContainerProps {
    heading: SubjectContainerHeading;
    footer?: SubjectContainerFooter;
    className?: string;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

export const SubjectsContainer: React.FC<React.PropsWithChildren<SubjectsContainerProps>> = props => {
    const contextualTheme = useTheme();
    const { heading, footer, className, children, theme = contextualTheme } = props;

    const { withRoundedCorners } = theme;

    return (
        <div
            className={classNames(
                styles.SubjectsContainer,
                withRoundedCorners && styles['Variant--rounded'],
                className
            )}
        >
            <div className={styles.Inner}>
                <div className={styles.Header}>
                    <div className={styles.HeaderLabel}>{heading.label}</div>
                    {heading.description && <div className={styles.HeaderDescription}>{heading.description}</div>}
                </div>
                {children}
            </div>
            {footer && (
                <div className={styles.Footer}>
                    {footer.label && <div className={styles.FooterDescription}>{footer.label}</div>}
                    <Button
                        className={styles.FooterAction}
                        type={footer.action.type}
                        tier={ButtonTier.Primary}
                        size={ButtonSize.ResponsiveFromSmallToLarge}
                        onClick={footer.action.onClick}
                    >
                        {footer.action.label}
                    </Button>
                </div>
            )}
        </div>
    );
};
